import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Grid, Button, Avatar, Stack,
  Radio, RadioGroup, FormControlLabel, LinearProgress, Snackbar, Alert,
  Tooltip,
  IconButton,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { motion, AnimatePresence } from 'framer-motion';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { setUserProfile } from '../../store/features/userProfileSlice';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../../utils/common';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';

// 1. Validation schema using Yup
const validationSchema = Yup.object({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  dob: Yup.string().required('Date of Birth is required'),
  gender: Yup.string().required('Gender is required'),
  designation: Yup.string().required('Designation is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phoneNo: Yup.number().typeError('Must be a number').required('Phone Number is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  pinCode: Yup.number().typeError('Must be a number').required('Pincode is required'),
  country: Yup.string().required('Country is required'),
});

function UserForm() {
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const [error, setError] = useState('');
  const [success, setSuccess] = useState();
  const user = useSelector(state => state.user);
  const fetchedUser = useSelector(state => state.userProfile?.data?.fetchedUsed);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [info, setInfo] = useState();
  const theme = useTheme();




  useEffect(() => {
    setInfo(!!fetchedUser ? "Update" : "Create");
  }, [fetchedUser]);

  // 2. Handle file upload
  const handleFileChange = (e, setFieldValue) => {
    setFieldValue('profilePhoto', e.target.files[0]);
  };

  const isEdit = !!fetchedUser;
  // console.log(isEdit, "isEdit");
  const initialValues = {
    profilePhoto: fetchedUser?.profilePhoto || '',
    firstName: fetchedUser?.firstName || '',
    lastName: fetchedUser?.lastName || '',
    dob: fetchedUser?.dob ? new Date(fetchedUser.dob) : null,
    gender: fetchedUser?.gender || '',
    designation: fetchedUser?.designation || '',
    email: fetchedUser?.email || '',
    phoneNo: fetchedUser?.phoneNo || '',
    socialLink: Array.isArray(fetchedUser?.socialLinks)
      ? fetchedUser?.socialLinks.join(', ')
      : (fetchedUser?.socialLinks || ''),
    city: fetchedUser?.city || '',
    state: fetchedUser?.state || '',
    pinCode: fetchedUser?.pinCode || '',
    country: fetchedUser?.country || '',
  };

  // 3. Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const socialArray = (() => {
      // if the value is already an array (unlikely here) use it
      if (Array.isArray(values.socialLink)) return values.socialLink;
      // if empty/string => create empty array
      if (!values.socialLink || !values.socialLink.trim()) return [];
      // split by comma, trim, and remove empty items
      return values.socialLink
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
    })();
    try {
      const data = new FormData();
      data.append('firstName', values?.firstName);
      data.append('lastName', values.lastName);
      data.append(
        'dob',
        values.dob ? format(new Date(values.dob), "yyyy-MM-dd") : ""
      );
      data.append('gender', values.gender);
      data.append('designation', values.designation);
      data.append('email', values?.email);
      data.append('phoneNo', Number(values.phoneNo));
      socialArray.forEach(link => data.append('socialLinks[]', link));
      data.append('city', values.city);
      data.append('state', values.state);
      data.append('pinCode', Number(values.pinCode));
      data.append('country', values.country);
      data.append('userName', user.userName);
      data.append('userId', user.id);
      if (values?.profilePhoto) {
        data.append('profilePhoto', values?.profilePhoto);
      }
      const url = isEdit
        ? `${apiUrl}/update-user`
        : `${apiUrl}/register`

      const method = isEdit ? 'put' : 'post';

      const res = await axios[method](url, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });



      if (res.data.status === "success") {
        // refresh profile in redux
        const updated = await axios.get(`${apiUrl}/user-details/${user.userName}`);
        dispatch(setUserProfile(updated.data));
        setSuccess(true);

        // NEW USER FIRST-TIME FLAG
        if (!isEdit) {
          const key = `first_time_template_${res.data.user.userId}`;
          localStorage.setItem(key, "true");
        }

        // fetch CVs
        const cvRes = await axios.get(`${apiUrl}/cv-details/${user.userName}`);
        // console.log(cvRes.data.fetchedCv.cvInfo, "fetchedCv");

        const defaultCv = cvRes.data.fetchedCv.cvInfo?.[0]; // first CV if exists

        if (isEdit) {
          // ✅ existing user → go to edit dashboard
          navigate("/edit");
        } else {
          // ✅ new user → open the first CV in edit mode if available
          if (defaultCv) {
            navigate(`/edit/add-group?groupId=${defaultCv.cvInfoId}&edit=true`);
          } else {
            // fallback: no CV yet, open create mode
            navigate("/edit/add-group?edit=false");
          }
        }

        resetForm();
      }
      setStep(1);

    } catch (err) {

      setError(`${err.response.data.error.errorResponse.errmsg}`);
      // setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  // UI rendering unchanged
  return (
    <Box sx={{
      bgcolor: theme.palette.background.backgroundColor,
      // minHeight: '100vh',
      display: 'flex',

      justifyContent: 'center',
      p: { xs: 2, sm: 4 }
    }}>
      <Paper elevation={3} sx={{
        p: { xs: 3, sm: 5 },
        borderRadius: 3,
        width: '100%',
        maxWidth: 900,
        my: 'auto'
      }}>
        <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>
          {info} Your Profile
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(step / totalSteps) * 100}
          sx={{ height: 8, borderRadius: 4, mb: 2 }}
        />
        <Typography variant="subtitle1" align="center" color="text.secondary" mb={2}>
          Step {step} of {totalSteps}
        </Typography>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
            <Form>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  {step === 1 && (
                    <>
                      <Stack spacing={3} alignItems="center" mb={2.8}>
                        <Box sx={{ position: 'relative' }}>
                          <Avatar
                            src={
                              typeof values?.profilePhoto === "string"
                                ? values?.profilePhoto
                                : values?.profilePhoto
                                  ? URL.createObjectURL(values?.profilePhoto)
                                  : ""
                            }
                            sx={{ width: 140, height: 140 }}
                          />
                          {values?.profilePhoto && (
                            <Tooltip title="Remove photo">
                              <IconButton
                                size="small"
                                onClick={() => setFieldValue('profilePhoto', '')}
                                sx={{
                                  position: 'absolute',
                                  top: -6,
                                  right: -6,
                                  bgcolor: 'background.paper',
                                  boxShadow: 1,
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Button variant="outlined" component="label">
                          Upload Photo
                          <input
                            type="file"
                            hidden
                            name="profilePhoto"
                            accept="image/*"
                            onChange={e => handleFileChange(e, setFieldValue)}
                          />
                        </Button>
                      </Stack>
                      <Grid container spacing={4}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="First Name"
                            name="firstName"
                            value={values.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.firstName && Boolean(errors.firstName)}
                            helperText={touched.firstName && errors.firstName}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Last Name"
                            name="lastName"
                            value={values.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.lastName && Boolean(errors.lastName)}
                            helperText={touched.lastName && errors.lastName}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="Date of Birth"
                              value={values.dob || null}
                              onChange={(newValue) => setFieldValue("dob", newValue)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: touched.dob && Boolean(errors.dob),
                                  helperText: touched.dob && errors.dob,
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Designation"
                            name="designation"
                            value={values.designation}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.designation && Boolean(errors.designation)}
                            helperText={touched.designation && errors.designation}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body1">Gender</Typography>
                          <RadioGroup
                            row
                            name="gender"
                            value={values.gender}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <FormControlLabel value="male" control={<Radio />} label="Male" />
                            <FormControlLabel value="female" control={<Radio />} label="Female" />
                            <FormControlLabel value="other" control={<Radio />} label="Other" />
                          </RadioGroup>
                          {touched.gender && errors.gender && (
                            <Typography color="error" variant="caption">{errors.gender}</Typography>
                          )}
                        </Grid>
                      </Grid>
                      <Box mt={4} display="flex" justifyContent="flex-end">
                        <Button
                          variant="contained"
                          onClick={() => setStep(2)}
                          sx={{ bgcolor: '#1a73e8' }}
                        >
                          Next
                        </Button>
                      </Box>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <Grid container spacing={4}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            name="phoneNo"
                            type="number"
                            value={values.phoneNo}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.phoneNo && Boolean(errors.phoneNo)}
                            helperText={touched.phoneNo && errors.phoneNo}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Social Link"
                            name="socialLink"
                            value={values.socialLink}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Grid>
                      </Grid>
                      <Box mt={4} display="flex" justifyContent="space-between">
                        <Button variant="outlined" onClick={() => setStep(1)}>
                          Previous
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => setStep(3)}
                          sx={{ bgcolor: '#1a73e8' }}
                        >
                          Next
                        </Button>
                      </Box>
                    </>
                  )}

                  {step === 3 && (
                    <>
                      <Grid container spacing={4}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="City"
                            name="city"
                            value={values.city}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.city && Boolean(errors.city)}
                            helperText={touched.city && errors.city}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="State"
                            name="state"
                            value={values.state}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.state && Boolean(errors.state)}
                            helperText={touched.state && errors.state}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Pincode"
                            type="number"
                            name="pinCode"
                            value={values.pinCode}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.pinCode && Boolean(errors.pinCode)}
                            helperText={touched.pinCode && errors.pinCode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Country"
                            name="country"
                            value={values.country}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.country && Boolean(errors.country)}
                            helperText={touched.country && errors.country}
                          />
                        </Grid>
                      </Grid>
                      <Box mt={4} display="flex" justifyContent="space-between">
                        <Button variant="outlined" onClick={() => setStep(2)}>
                          Previous
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          sx={{ bgcolor: '#1a73e8' }}
                          disabled={isSubmitting}
                        >
                          Save Profile
                        </Button>
                      </Box>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </Form>
          )}
        </Formik>

        <Snackbar open={!!error} autoHideDuration={2000} onClose={() => setError(false)}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar open={success} autoHideDuration={10000} onClose={() => setSuccess(false)}>
          <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
            Profile saved successfully!
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
}

export default UserForm;
